import { SERVICE, ServiceType } from '@src/api/types/shared/serviceType';
import { SESSION_STORAGE_KEYS } from '@src/constants/sessionStorage';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { CompanyRoleType } from '@src/enum/companyRoleEnum';
import { TenantRoleType } from '@src/enum/tenantRoleEnum';
import { useAccessibleChannels } from '@src/hooks/useAccessibleChannels';
import { useChannelsStatus } from '@src/hooks/useChannelStatus';
import Error404 from '@src/pages/Error404';
import { COMPANY_URL } from '@src/routes/company';
import { TENANT_URL } from '@src/routes/tenant';
import { useParams } from 'react-router-dom';

type BaseProps = {
  children: React.ReactNode;
  canService?: ServiceType[];
  checkPublicChannel?: boolean;
  checkAccessibleNotPublicChannel?: boolean;
  checkAuthenticate?: boolean;
  checkStatus?: boolean;
  redirectToErrorPageWhenNotAuthenticated?: boolean;
  checkHasParticipatingChannels?: boolean;
};

type TenantProps = BaseProps & {
  canRoles?: TenantRoleType[];
};

type CompanyProps = BaseProps & {
  canRoles?: CompanyRoleType[];
};

export type Profile = {
  isLogin: boolean;
  user?: { role: { code: string }; enabled: boolean };
  tenant?: { service?: Record<string, boolean> };
};

const CommunityGuard: React.FC<
  BaseProps & {
    profile: Profile | undefined | null;
    isTenant: boolean;
    canRoles?: string[];
    isProfileLoading?: boolean;
  }
> = ({
  children,
  profile,
  isTenant,
  canRoles,
  canService,
  checkPublicChannel = false,
  checkAccessibleNotPublicChannel = false,
  checkAuthenticate = false,
  checkStatus = false,
  redirectToErrorPageWhenNotAuthenticated = false,
  checkHasParticipatingChannels = false,
  isProfileLoading = false,
}) => {
  const { code } = useParams<{ code: string }>();
  const { accessibleChannels, isLoading } = useAccessibleChannels(profile?.isLogin);
  const isExist = useChannelsStatus(code!, checkStatus);

  // Show loading when profile is still being fetched or when accessibleChannels is loading
  if (isProfileLoading || !profile || (profile?.isLogin && isLoading)) {
    return null;
  }

  // チャンネルが存在しない場合は404を返す
  if (checkStatus) {
    if (isLoading) {
      return null;
    }
    if (!isExist) {
      return <Error404 isTenant={isTenant} />;
    }
  }

  if (checkAuthenticate && !profile.isLogin) {
    // Don't redirect if still loading channels data
    if (isLoading) {
      return null;
    }

    if (redirectToErrorPageWhenNotAuthenticated) {
      return <Error404 isTenant={isTenant} />;
    }

    const currentUrl = window.location.pathname + window.location.search;
    sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN, currentUrl);

    const urlParams = new URLSearchParams(window.location.search);
    const userTypeFromEmail = urlParams.get('user_type');

    const shouldUseTenantLogin =
      userTypeFromEmail === 'tenant' || (userTypeFromEmail !== 'tenant' && isTenant);

    if (shouldUseTenantLogin) {
      window.location.href = `${TENANT_URL.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`;
    } else {
      window.location.href = `${COMPANY_URL.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`;
    }
    return null;
  }

  if (checkAuthenticate && !profile.user) {
    return;
  }

  if (canRoles) {
    const hasRequiredRole = canRoles.some((role) => profile?.user?.role.code === role);
    if (!hasRequiredRole || !profile?.user?.enabled) {
      return <Error404 isTenant={isTenant} />;
    }
  }

  if (canService) {
    const hasRequiredService = canService.some((service) => {
      if (service === SERVICE.ENTERPRISE_PORTAL) {
        return profile?.tenant?.service?.enterprise_portal;
      }
      if (service === SERVICE.DX_DIAGNOSIS) {
        return profile?.tenant?.service?.dx_diagnosis;
      }
      if (service === SERVICE.ONLINE_COMMUNITY) {
        return profile?.tenant?.service?.online_community;
      }

      return false;
    });

    if (!hasRequiredService) {
      return <Error404 isTenant={isTenant} />;
    }
  }

  if (checkPublicChannel) {
    if (isLoading) {
      return null;
    }
    if (accessibleChannels?.channel.public.includes(code!)) {
      return <Error404 isTenant={isTenant} />;
    }
  }

  if (checkHasParticipatingChannels && accessibleChannels) {
    if (isLoading) {
      return null;
    }
    const hasNoParticipatingChannels =
      (!accessibleChannels?.channel.public || accessibleChannels.channel.public.length === 0) &&
      (!accessibleChannels?.channel.not_public ||
        accessibleChannels.channel.not_public.length === 0);

    if (hasNoParticipatingChannels) {
      return <Error404 isTenant={isTenant} />;
    }
  }

  if (checkAccessibleNotPublicChannel) {
    if (isLoading) {
      return null;
    }
    if (
      !accessibleChannels?.channel.not_public.includes(code!) &&
      !accessibleChannels?.channel.public.includes(code!)
    ) {
      return <Error404 isTenant={isTenant} />;
    }
  }

  return <>{children}</>;
};

const TenantCommunityGuard: React.FC<TenantProps> = ({ canRoles, ...props }) => {
  const tenantAuth = useTenantAuth();
  const { profile } = tenantAuth;
  return (
    <CommunityGuard
      {...props}
      profile={profile ?? null}
      isTenant={true}
      canRoles={canRoles as string[]}
    />
  );
};

const CompanyCommunityGuard: React.FC<CompanyProps> = ({ canRoles, ...props }) => {
  const companyAuth = useCompanyAuth();
  const { profile } = companyAuth;
  return (
    <CommunityGuard
      {...props}
      profile={profile ?? null}
      isTenant={false}
      canRoles={canRoles as string[]}
    />
  );
};

export { CommunityGuard, CompanyCommunityGuard, TenantCommunityGuard };
